import { type ReactNode } from 'react';
import { Spinner } from '@monksflow/monks-ui';
import type { ProjectListItem } from '@/entities/project';
import { useTranslation } from 'react-i18next';

interface ProjectPickerContentProps {
  projects: ProjectListItem[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  searchQuery: string;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  renderProject: (project: ProjectListItem) => ReactNode;
  additionalLoadingCheck?: boolean;
}

export function ProjectPickerContent({
  projects,
  isLoading,
  isFetchingNextPage,
  searchQuery,
  sentinelRef,
  renderProject,
  additionalLoadingCheck = false,
}: ProjectPickerContentProps) {
  const { t } = useTranslation('common');

  return (
    <div className="pb-2 overflow-y-auto">
      {isLoading || additionalLoadingCheck ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" className="text-muted-foreground" />
        </div>
      ) : projects.length === 0 ? (
        // The empty-list treatment of Tessra's `CommandEmpty`, in the muted tone of its
        // `ComboboxEmpty`: this is a search over a list, and the message stands where rows would.
        <p className="px-2 py-6 text-center text-sm text-muted-foreground">
          {searchQuery.trim()
            ? t('ui.projectPickerContent.noProjectsFoundMatching', { searchQuery })
            : t('ui.projectPickerContent.noProjectsFound')}
        </p>
      ) : (
        <>
          {projects.map((project) => renderProject(project))}
          <div ref={sentinelRef} className="h-1" />
          {isFetchingNextPage && (
            <div className="flex justify-center py-2">
              <Spinner className="text-muted-foreground" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
